import React, { useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import ReactSelect, { components } from 'react-select';
import { useIntl } from 'react-intl';
import { useWindowSize } from '@react-hook/window-size';

import { useTheme } from '../../ThemeContext';

import {
  Control as CustomControl,
  SelectContainer as CustomSelectContainer,
  Placeholder as CustomPlaceholder,
  ValueContainer as CustomValueContainer,
  SingleValue as CustomSingleValue,
  Input as CustomInput,
  Menu as CustomMenu,
  MenuList as CustomMenuList,
  Option as CustomOption,
  IndicatorSeparator as CustomIndicatorSeparator,
  IndicatorsContainer as CustomIndicatorsContainer,
  NoOptionsMessage as CustomNoOptionsMessage,
  DropdownIndicator as CustomDropdownIndicator,
  DropdownIndicatorIcon as CustomDropdownIndicatorIcon,
  MultiValueLabel as CustomMultiValueLabel,
  MultiValueContainer as CustomMultiValueContainer,
  Checkbox as CustomCheckbox,
  CheckboxInput as CustomCheckboxInput,
  CheckboxCheckmark as CustomCheckboxCheckmark,
  Radio as CustomRadio,
  RadioInput as CustomRadioInput,
  RadioCheckmark as CustomRadioCheckmark,
} from './Select.style';

const SelectContainer = (props) => {
  return (
    <CustomSelectContainer
      ref={props.innerRef}
      {...props.innerProps}
      disabled={props.selectProps.isDisabled}
    >
      {props.children}
    </CustomSelectContainer>
  );
};

const Control = (props) => {
  const theme = useTheme();
  return (
    <CustomControl
      theme={theme.select.control}
      ref={props.innerRef}
      {...props.innerProps}
      invalid={props.selectProps.invalid}
      valid={props.selectProps.valid}
      small={props.selectProps.small}
    >
      {props.children}
    </CustomControl>
  );
};

const Placeholder = (props) => {
  const theme = useTheme();
  return (
    <CustomPlaceholder
      theme={theme.select.placeholder}
      ref={props.innerRef}
      {...props.innerProps}
      small={props.selectProps.small}
    >
      {props.children}
    </CustomPlaceholder>
  );
};

const SingleValue = (props) => {
  const theme = useTheme();
  return (
    <CustomSingleValue
      theme={theme.select.singleValue}
      ref={props.innerRef}
      {...props.innerProps}
      invalid={props.selectProps.invalid}
      small={props.selectProps.small}
    >
      {props.children}
    </CustomSingleValue>
  );
};

const Input = (props) => {
  const theme = useTheme();
  return (
    <CustomInput
      theme={theme.select.input}
      size={props.selectProps.size}
      filled={props.selectProps.inputValue}
    >
      <components.Input {...props} />
    </CustomInput>
  );
};

const NoOptionsMessage = (props) => {
  const theme = useTheme();
  return (
    <CustomNoOptionsMessage
      theme={theme.select.noOptionsMessage}
      ref={props.innerRef}
      {...props.innerProps}
      small={props.selectProps.small}
    >
      {props.children}
    </CustomNoOptionsMessage>
  );
};

const ValueContainer = (props) => {
  const intl = useIntl();
  const theme = useTheme();

  return (
    <CustomValueContainer ref={props.innerRef} {...props.innerProps}>
      {props.selectProps.isMulti ? (
        <CustomMultiValueContainer theme={theme.select.label}>
          {props.children}
          {props.selectProps.value && props.selectProps.value.length > 1 ? (
            <CustomMultiValueLabel>
              {intl.formatMessage({ id: 'SELECT.MULTIPLE_VALUES' })} (
              {
                props.selectProps.value
                  .filter((option) => option.value && option.value !== '*')
                  .map((option) => option.value).length
              }
              )
            </CustomMultiValueLabel>
          ) : (
            ''
          )}
        </CustomMultiValueContainer>
      ) : (
        props.children
      )}
    </CustomValueContainer>
  );
};

const Menu = (props) => {
  return (
    <CustomMenu ref={props.innerRef} {...props.innerProps}>
      {props.children}
    </CustomMenu>
  );
};

const MenuList = (props) => {
  const theme = useTheme();
  return (
    <CustomMenuList
      theme={theme.select.menuList}
      ref={props.innerRef}
      {...props.innerProps}
    >
      {props.children}
    </CustomMenuList>
  );
};

const Option = (props) => {
  const theme = useTheme();
  return (
    <CustomOption
      theme={theme.select.option}
      ref={props.innerRef}
      {...props.innerProps}
      small={props.selectProps.small}
      selected={props.isSelected ? true : undefined}
    >
      {props.value && props.selectProps.isCheckbox ? (
        <>
          <CustomCheckbox
            theme={theme.select.checkbox.input}
            visible={props.isSelected ? true : undefined}
          >
            <CustomCheckboxInput
              type='checkbox'
              checked={props.isSelected ? true : undefined}
              readOnly
            />
            <CustomCheckboxCheckmark
              theme={theme.select.checkbox.checkmark}
              className='material-icons'
              visible={props.isSelected ? true : undefined}
            >
              check
            </CustomCheckboxCheckmark>
          </CustomCheckbox>
          {props.children}
        </>
      ) : props.value && props.selectProps.isRadio ? (
        <>
          <CustomRadio
            theme={theme.select.radio.input}
            visible={props.isSelected ? true : undefined}
          >
            <CustomRadioInput
              type='radio'
              checked={props.isSelected ? true : undefined}
              readOnly
            />
            <CustomRadioCheckmark
              theme={theme.select.radio.checkmark}
              visible={props.isSelected ? true : undefined}
            />
          </CustomRadio>
          {props.children}
        </>
      ) : (
        props.children
      )}
    </CustomOption>
  );
};

const IndicatorSeparator = (props) => (
  <CustomIndicatorSeparator ref={props.innerRef} {...props.innerProps} />
);

const IndicatorsContainer = (props) => (
  <CustomIndicatorsContainer
    ref={props.innerRef}
    {...props.innerProps}
    small={props.selectProps.small}
  >
    {props.children}
  </CustomIndicatorsContainer>
);

const DropdownIndicator = (props) => {
  const theme = useTheme();
  return (
    <CustomDropdownIndicator
      ref={props.innerRef}
      {...props.innerProps}
      small={props.selectProps.small}
    >
      <CustomDropdownIndicatorIcon
        theme={theme.select.indicatorIcon}
        className='material-icons'
        small={props.selectProps.small}
      >
        arrow_drop_down
      </CustomDropdownIndicatorIcon>
    </CustomDropdownIndicator>
  );
};

const MultiValue = (props) => {
  return (
    <>
      {props.children &&
      props.selectProps.value &&
      props.selectProps.value.length <= 1 ? (
        <CustomMultiValueLabel ref={props.innerRef} {...props.innerProps}>
          {props.children}
        </CustomMultiValueLabel>
      ) : (
        ''
      )}
    </>
  );
};

const Select = ({
  options = [],
  value,
  onChange = () => {},
  onBlur = () => {},
  isLoading = false,
  isSearchable = false,
  isDisabled = false,
  isMulti = false,
  isSelectAll = false,
  isCheckbox = false,
  isRadio = false,
  closeMenuOnSelect = true,
  loadingMessage = 'SELECT.LOADING',
  placeholderMessage = 'SELECT.PLACEHOLDER',
  noOptionsMessage = 'SELECT.NO_OPTIONS',
  invalid = false,
  valid = false,
  small = false,
  menuPosition = 'fixed',
}) => {
  const intl = useIntl();
  const [width] = useWindowSize();
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const isAllOptionsSelected = () => {
    let isSelected = false;
    if (value) {
      const selectedOptions = value.split(',');
      isSelected = true;
      options.forEach((option) => {
        if (!selectedOptions.includes(String(option.value))) {
          isSelected = false;
        }
      });
    }
    return isSelected;
  };

  useEffect(() => {
    forceUpdate();
  }, [width]);

  return (
    <ReactSelect
      menuPosition={menuPosition}
      menuPlacement='auto'
      isDisabled={isDisabled ? true : undefined}
      placeholder={intl.formatMessage({
        id: placeholderMessage,
        defaultMessage: placeholderMessage,
      })}
      noOptionsMessage={() =>
        isLoading
          ? intl.formatMessage({
              id: loadingMessage,
              defaultMessage: loadingMessage,
            })
          : intl.formatMessage({
              id: noOptionsMessage,
              defaultMessage: noOptionsMessage,
            })
      }
      isSearchable={isSearchable}
      value={
        options && options.length > 0
          ? isSelectAll && isAllOptionsSelected()
            ? [
                {
                  value: '*',
                },
                ...options.filter((option) => {
                  if (option.value !== undefined && value !== undefined) {
                    if (isMulti) return value.includes(String(option.value));
                    else return String(option.value) === String(value);
                  }
                  return undefined;
                }),
              ]
            : options.filter((option) => {
                if (value !== undefined) {
                  if (isMulti) {
                    const selectedOptions = value.split(',');
                    return selectedOptions.includes(String(option.value));
                  } else return String(option.value) === String(value);
                }
                return undefined;
              })
          : undefined
      }
      options={
        options && options.length > 0
          ? isSelectAll
            ? [
                {
                  label: intl.formatMessage({
                    id: 'SELECT.SELECT_ALL',
                  }),
                  value: '*',
                },
                ...options,
              ]
            : options
          : []
      }
      onChange={(selectedOption, actionMeta) => {
        if (isMulti)
          onChange(
            selectedOption && selectedOption.length > 0
              ? actionMeta.option && actionMeta.option.value === '*'
                ? actionMeta.action === 'select-option'
                  ? options
                      .filter((option) => option.value && option.value !== '*')
                      .map((option) => option.value)
                      .join(',')
                  : undefined
                : selectedOption
                    .filter((option) => option.value && option.value !== '*')
                    .map((option) => option.value)
                    .join(',')
              : undefined
          );
        else onChange(selectedOption && selectedOption.value);
      }}
      onBlur={() => {
        onBlur();
      }}
      components={{
        SelectContainer,
        Control,
        Placeholder,
        ValueContainer,
        SingleValue,
        Input,
        Menu,
        MenuList,
        Option,
        IndicatorSeparator,
        IndicatorsContainer,
        NoOptionsMessage,
        DropdownIndicator,
        MultiValue,
      }}
      invalid={invalid}
      valid={valid}
      small={small}
      isMulti={isMulti}
      isCheckbox={isCheckbox}
      isRadio={isRadio}
      isClearable={false}
      hideSelectedOptions={false}
      closeMenuOnSelect={closeMenuOnSelect}
    />
  );
};

Select.propTypes = {
  options: PropTypes.array,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  isLoading: PropTypes.bool,
  isSearchable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isMulti: PropTypes.bool,
  isSelectAll: PropTypes.bool,
  isCheckbox: PropTypes.bool,
  isRadio: PropTypes.bool,
  closeMenuOnSelect: PropTypes.bool,
  loadingMessage: PropTypes.string,
  placeholderMessage: PropTypes.string,
  noOptionsMessage: PropTypes.string,
  invalid: PropTypes.bool,
  valid: PropTypes.bool,
  small: PropTypes.bool,
  menuPosition: PropTypes.string,
};

export default Select;
